import type { AgentEditChromeData } from '@atlaskit/editor-common/collab-agent-edit-chrome';
import { isExperimentEnabled } from '@atlaskit/platform-feature-experiments/is-experiment-enabled';

import type { AgentInfo } from '../decoration/reducer';
import { resolveTelepointerPresentation } from '../../ui/components/TelepointerAvatar/telepointerPresentation';

type AgentEditChromePresentation = Pick<
	AgentEditChromeData,
	| 'agentId'
	| 'agentIdentityAccountId'
	| 'agentName'
	| 'agentNamedId'
	| 'highlightBackgroundColor'
	| 'highlightBorderColor'
	| 'telepointerAgentKind'
	| 'telepointerLabel'
	| 'telepointerLineColor'
>;

/**
 * Resolves optional agent presentation at the producer boundary.
 *
 * Shares `resolveTelepointerPresentation` / `TelepointerAvatar` with the frontend
 * in-editor-direct-streaming path, behind the same `platform_editor_agent_telepointer_redesign`
 * experiment, so the collab/BE-streamed telepointer renders the identical avatar and colour
 * treatment instead of the old id-only, avatar-less design that was gated on the now-removed
 * `platform_editor_agent_mentions_telepointer_avatar` feature gate.
 */
export const getAgentEditChromePresentation = (
	agentInfo?: AgentInfo,
): AgentEditChromePresentation => {
	if (!isExperimentEnabled('platform_editor_agent_mentions')) {
		return {};
	}

	if (isExperimentEnabled('platform_editor_agent_telepointer_redesign') && agentInfo) {
		const presentation = resolveTelepointerPresentation(agentInfo);
		return {
			agentId: agentInfo.agentId,
			agentIdentityAccountId: agentInfo.agentIdentityAccountId ?? undefined,
			agentName: agentInfo.agentName,
			agentNamedId: agentInfo.agentNamedId ?? undefined,
			highlightBackgroundColor: presentation.highlightBackgroundColor,
			highlightBorderColor: presentation.highlightBorderColor,
			telepointerAgentKind: presentation.telepointerAgentKind,
			telepointerLineColor: presentation.telepointerLineColor,
		};
	}

	return agentInfo?.agentName ? { telepointerLabel: agentInfo.agentName } : {};
};
